import { useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ShadowAiToolUsersSource } from '@kirin/api-client/contracts/shadow-ai';

import { UserListModal } from '@/components/UserListModal';
import { useToolUsers } from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { usersEndpointsLabel } from '@/components/shared/shadow-ai/usersEndpointsLabel';

import { buildAiToolUserColumns, type AIToolUser } from './constants';

interface ToolInfo {
  toolId: string;
  name: string;
  riskLevel?: string;
  userCount: number;
  /** KIR-5281: the widget row's category, so the list is that row's users. */
  category?: string;
}

interface AIToolUserListModalProps {
  open: boolean;
  onClose: () => void;
  tool: ToolInfo | null;
  /** KIR-5281: the widget's source (Browser / Installed), when it has one. */
  source?: ShadowAiToolUsersSource;
  emptyStateMessage?: string;
}

export function AIToolUserListModal({
  open,
  onClose,
  tool,
  source,
  emptyStateMessage = 'No users found for this tool',
}: AIToolUserListModalProps): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const toolUsersQuery = useToolUsers(
    organizationId,
    tool?.toolId ?? undefined,
    open,
    undefined,
    { category: tool?.category, source }
  );
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const columns = useMemo(
    () => buildAiToolUserColumns(endpointEnabled),
    [endpointEnabled]
  );

  // Transform the data to match our column structure
  const transformedData = toolUsersQuery.data?.users ?? [];
  const query = {
    ...toolUsersQuery,
    data: transformedData,
  } as unknown as UseQueryResult<AIToolUser[]>;

  if (!tool) {
    return null;
  }

  return (
    <UserListModal<AIToolUser>
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
      title={
        <>
          Users of <span className="font-mono">{tool.name}</span>
          {transformedData.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {usersEndpointsLabel(transformedData)}
            </span>
          )}
        </>
      }
      query={query}
      columns={columns}
      emptyState={emptyStateMessage}
    />
  );
}
